import { useMemo, useState } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { Page } from '../../components/Page/Page';
import Loader from '../../components/Loader/Loader';
import { t } from 'i18next';
import Arrow from '../../assets/imgs/chats/arrow.svg';
import { SDrawerWrapper } from '../../components/cec/CecTextTemplatesBottomSheet/CecTextTemplatesBottomSheet';
import { TimeSelectionBottomSheet } from '../../components/TimeSelectionBottomSheet/TimeSelectionBottomSheet';
import { DateSelectionBottomSheet } from '../../components/DateSelectionBottomSheet/DateSelectionBottomSheet';
import {
  SActivateButton,
  SActivateButtonContainer,
  SCheckBoxWithSubTitle,
  SDescription,
  SIcon,
  SInputContainer,
  SLabel,
  SlidingContainer,
  SPage,
} from './SetAvailability.styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateUser } from '../../api/users';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { refreshUser } from '../Login/LoginSlice/actionCreators';
import { selectUser } from '../Login/LoginSlice';
import { selectTenant } from '../Organizations/OrganizationsSlice';
import { translate } from '../../utils/translate';
import {
  canDisplayConfirm,
  parseUnavailableDate,
  parseUnavailableTime,
} from './SetAvailability.helpers';
import { getDateTextByLanguage } from '../../components/SidebarAvailabilityButton/helpers';
import { getNumbersBasedDateTextByLanguage } from '../../utils/date';
import { checkIsAvailable } from '../../utils/helperFunctions';

export type Time = {
  hours: number;
  minutes: number;
};

export const SetAvailability = () => {
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUser);
  const tenant = useAppSelector(selectTenant);
  const isCurrentlyAvailable = checkIsAvailable(user?.unavailableUntil ?? null);

  const navigate = useNavigate();
  const location = useLocation();
  const prevPath = location.state?.prevPath;

  const [isLoading, setIsLoading] = useState(false);
  const [isAvailable, setIsAvailable] = useState(isCurrentlyAvailable);

  const [date, setDate] = useState<string | undefined>(
    !isCurrentlyAvailable && user?.unavailableUntil
      ? parseUnavailableDate(user.unavailableUntil)
      : undefined
  );
  const [time, setTime] = useState<Time>(
    !isCurrentlyAvailable && user?.unavailableUntil
      ? parseUnavailableTime(user.unavailableUntil)
      : { hours: 0, minutes: 0 }
  );

  const [isTimeSelectionOpen, setIsTimeSelectionOpen] = useState(false);
  const [isDateSelectionOpen, setIsDateSelectionOpen] = useState(false);

  const dateText = useMemo(() => {
    if (!date) return t('date');
    const dateParts = date.split('-');
    const dateTime = new Date(
      Number(dateParts[0]),
      Number(dateParts[1]) - 1,
      Number(dateParts[2]),
      12,
      0
    );

    return getNumbersBasedDateTextByLanguage({
      dateText: dateTime.toISOString(),
      excludeTime: true,
    });
  }, [date]);

  const displayConfirm = useMemo(
    () => canDisplayConfirm(isAvailable, date, time, user),
    [date, time, isAvailable, user?.unavailableUntil]
  );

  const timeText = `${time?.hours > 9 ? time?.hours : `0${time.hours}`}:${
    time?.minutes > 9 ? time?.minutes : `0${time.minutes}`
  }`;

  const handleSetTime = (hours: number, minutes: number) => {
    setTime({ hours, minutes });
    setIsTimeSelectionOpen(false);
  };

  const handleSetDate = (dateString: string | undefined) => {
    setDate(dateString);
    setIsDateSelectionOpen(false);
  };

  const handleConfirmation = async () => {
    if (!user || !tenant) {
      throw new Error('Cannot save availability when tenant or user is missing');
    }

    try {
      setIsLoading(true);

      if (isAvailable) {
        await updateUser({
          userId: user.id,
          tenantId: tenant.id,
          body: { unavailableUntil: null },
        });
      } else if (date) {
        const dateTime = new Date(`${date}T00:00:00`);
        dateTime.setHours(time.hours, time.minutes, 0, 0);
        const unavailableUntil = dateTime.toISOString();
        await updateUser({
          userId: user?.id,
          tenantId: tenant.id,
          body: { unavailableUntil },
        });
      }
      await dispatch(refreshUser());
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      navigate(prevPath ?? '/sidebar');
    }
  };

  const handleSelect = (is: boolean) => {
    setIsAvailable(is);
  };

  if (isLoading || !user) {
    return (
      <Layout isMessageLayout message="profile_not_available_mode_title" to="/sidebar/settings">
        <Page>
          <Loader />
        </Page>
      </Layout>
    );
  }

  return (
    <Layout isMessageLayout message="profile_not_available_mode_title" to={prevPath ?? '/sidebar'}>
      <SPage>
        <div className="mainContainer">
          <SDescription>
            {isCurrentlyAvailable
              ? t<string>('profile_not_available_mode_description')
              : t<string>('profile_not_available_until', {
                  dateTime: getDateTextByLanguage(user.unavailableUntil),
                })}
          </SDescription>

          <SCheckBoxWithSubTitle
            selected={isAvailable}
            title={translate('profile_available')}
            onToggleCheck={() => handleSelect(true)}
            valueId={0}
            checkBoxType={'circle'}
            withoutSeparator
            clickAll={true}
            startWithCheckBox
          />
          <SCheckBoxWithSubTitle
            selected={!isAvailable}
            title={translate('profile_not_available')}
            onToggleCheck={() => handleSelect(false)}
            valueId={0}
            checkBoxType={'circle'}
            withoutSeparator
            clickAll={true}
            startWithCheckBox
          />
          {!isAvailable && (
            <SlidingContainer>
              <SLabel>{t<string>('profile_end_date')}:</SLabel>
              <SInputContainer onClick={() => setIsDateSelectionOpen(true)}>
                <p>{dateText}</p>
                <SIcon src={Arrow} alt="" role="arrowIcon" />
              </SInputContainer>
              <SLabel>{t<string>('profile_end_time')}:</SLabel>
              <SInputContainer onClick={() => setIsTimeSelectionOpen(true)}>
                <p>{timeText}</p>
                <SIcon src={Arrow} alt="" role="arrowIcon" />
              </SInputContainer>
            </SlidingContainer>
          )}
        </div>
        {displayConfirm && (
          <SActivateButtonContainer role="activateButtonContainer">
            <SActivateButton onClick={handleConfirmation} role="activateButton">
              {t<string>('confirm')}
            </SActivateButton>
          </SActivateButtonContainer>
        )}
        <SDrawerWrapper role="drawerWrapper">
          <TimeSelectionBottomSheet
            isOpen={isTimeSelectionOpen}
            setIsOpen={setIsTimeSelectionOpen}
            activeCountdownSetup={handleSetTime}
            initialHours={time?.hours}
            initialMinutes={time?.minutes}
            titleTx="profile_select_time"
          />
        </SDrawerWrapper>
        <SDrawerWrapper role="drawerWrapper">
          <DateSelectionBottomSheet
            isOpen={isDateSelectionOpen}
            setIsOpen={setIsDateSelectionOpen}
            handleConfirm={handleSetDate}
            initialDate={date}
          />
        </SDrawerWrapper>
      </SPage>
    </Layout>
  );
};
